import { createTheme } from '@mui/material/styles';

// Global
import { breakpoints } from './theme/global/breakpoints';
import { palette } from './theme/global/palette';
import { typography } from './theme/global/typography';
import { MuiCssBaseline } from './theme/global/baseLine';
import { shape } from './theme/global/shape';

// Components
import { MuiAlert } from './theme/components/alert';
import { MuiAlertTitle } from './theme/components/alertTitle';
import { MuiAccordion } from './theme/components/accordion';
import { MuiAccordionDetails } from './theme/components/accordionDetails';
import { MuiAccordionSummary } from './theme/components/accordionSummary';
import { MuiBackdrop } from './theme/components/backdrop';
import { MuiBadge } from './theme/components/badge';
import { MuiBreadcrumbs } from './theme/components/breadcrumbs';
import { MuiButton } from './theme/components/button';
import { MuiSvgIcon } from './theme/components/svgIcon';
import { MuiCardContent } from './theme/components/cardContent';
import { MuiCheckbox } from './theme/components/checkbox';
import { MuiChip } from './theme/components/chip';
import { MuiDialogTitle } from './theme/components/dialogTitle';
import { MuiDialogActions } from './theme/components/dialogActions';
import { MuiDialogContent } from './theme/components/dialogContent';
import { MuiDialogContentText } from './theme/components/dialogContentText';
import { MuiFab } from './theme/components/floatingActionButton';
import { MuiFormHelperText } from './theme/components/formHelperText';
import { MuiIconButton } from './theme/components/iconButton';
import { MuiInputLabel } from './theme/components/inputLabel';
import { MuiInputAdornment } from './theme/components/inputAdornment';
import { MuiLink } from './theme/components/link';
import { MuiListItemButton } from './theme/components/listItemButton';
import { MuiListItemIcon } from './theme/components/listItemIcon';
import { MuiMenu } from './theme/components/menu';
import { MuiMenuItem } from './theme/components/menuItem';
import { MuiOutlinedInput } from './theme/components/outlinedInput';
import { MuiPagination } from './theme/components/pagination';
import { MuiPaginationItem } from './theme/components/paginationItem';
import { MuiPopover } from './theme/components/popover';
import { MuiProgressIndicators } from './theme/components/progressIndicators';
import { MuiRadio } from './theme/components/radio';
import { MuiSelect } from './theme/components/select';
import { MuiSlider } from './theme/components/slider';
import { MuiSwitch } from './theme/components/switch';
import { MuiTab } from './theme/components/tab';
import { MuiTabs } from './theme/components/tabs';
import { MuiTextField } from './theme/components/textField';
import { MuiTooltip } from './theme/components/tooltip';
import { MuiStepper } from './theme/components/stepper';

const tdcTheme = createTheme({
  palette,
  typography,
  shape,
  breakpoints,
  components: {
    ...MuiAccordion,
    ...MuiAccordionDetails,
    ...MuiAccordionSummary,
    ...MuiAlert,
    ...MuiAlertTitle,
    ...MuiCssBaseline,
    ...MuiBackdrop,
    ...MuiBadge,
    ...MuiBreadcrumbs,
    ...MuiButton,
    ...MuiCardContent,
    ...MuiSvgIcon,
    ...MuiCheckbox,
    ...MuiChip,
    ...MuiDialogTitle,
    ...MuiDialogContent,
    ...MuiDialogContentText,
    ...MuiDialogActions,
    ...MuiFab,
    ...MuiFormHelperText,
    ...MuiIconButton,
    ...MuiInputLabel,
    ...MuiInputAdornment,
    ...MuiLink,
    ...MuiListItemButton,
    ...MuiListItemIcon,
    ...MuiMenu,
    ...MuiMenuItem,
    ...MuiOutlinedInput,
    ...MuiPagination,
    ...MuiPaginationItem,
    ...MuiPopover,
    ...MuiProgressIndicators,
    ...MuiRadio,
    ...MuiTooltip,
    ...MuiSelect,
    ...MuiSlider,
    ...MuiStepper,
    ...MuiSwitch,
    ...MuiTab,
    ...MuiTabs,
    ...MuiTextField,
  },
});

// Custom variant declarations
declare module '@mui/material/styles' {
  interface Palette {
    orange?: Palette['primary'];
    // Upstream types these as Palette['primary'] but assigns bare strings in palette.ts. The
    // mismatch never surfaced there because the augmentation named
    // '@mui/material/styles/createPalette', a path MUI 7 no longer exposes, so it was silently
    // ignored. Typed as what they actually are.
    accent?: string;
    accentDark?: string;
  }
  interface PaletteOptions {
    orange?: PaletteOptions['primary'];
    accent?: string;
    accentDark?: string;
  }
}
declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    inline: true;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    invertedOutlined: true;
    invertedText: true;
    brand: true;
  }
}

export default tdcTheme;
